/**
 * Staff-ui ArticleForm (create + edit).
 *
 * Aligned with the canonical account-ui `ArticleForm`
 * (`services/account-ui/src/pages/Profiles/ArticleForm/ArticleForm.tsx`) via the
 * shared staff-ui primitives in `@/components/Profiles` (StatusPill, SaveActions,
 * FormValidationSummary, useFormValidation, RichTextField, ImageUploadField,
 * LanguageTabBar + useLanguageVersions/useLanguageHotkeys). Status is driven by
 * which SaveActions button is pressed — there is no editable status field — and
 * Publish is gated behind `profiles.publish`.
 *
 * Justified deviations from account-ui (documented inline):
 *  - Regions use the shared staff-ui `RegionsPicker` (not account-ui's
 *    `RegionPicker`). staff-ui standardised on `RegionsPicker` (see
 *    `StaffFirmProfileForm`); this is the reconcile resolution for the regions
 *    divergence.
 *  - The article type is LOCKED (read-only pill). staff routes are
 *    `/profiles/articles/:id` with no `:articleType` segment, so in create mode
 *    the type is chosen in `New.tsx` (alongside the CompanyPicker) and passed in
 *    as `initialArticleType`; inside the form it is a locked pill in both modes.
 *  - The interview-contact picker is the bespoke staff-only, company-scoped
 *    debounced typeahead (kept as-is — functional and scoped to the firm), not
 *    account-ui's shared `Typeahead`.
 *  - Editorial categories use the shared `EditorialCategorySelect` typeahead
 *    (global practice-area search + id hydration for chips) and are sent on
 *    save for DBI / focus_on / legal_development / interview articles.
 */
import {
  Button,
  EditorialCategorySelect,
  type EditorialCategoryResult,
  Input,
  Select,
  type SelectOption,
} from "@legal-500/legal500-design-system";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { z } from "zod";
import {
  type Article,
  type ArticleCreatePayload,
  type ArticleUpdatePayload,
  articleKeys,
  articlesService,
  type ContentStatus,
  extractZodIssues,
  type ImageRef,
  type LawyerProfile,
  lawyerProfileKeys,
  lawyerProfilesService,
  type RegionAssignment,
  type StaffArticleHydration,
  type StaffArticleType,
} from "@/api/profiles";
import { ConfirmationModal } from "@/components/Modal/ConfirmationModal";
import {
  FormValidationSummary,
  ImageUploadField,
  labelForLanguage,
  LanguageTabBar,
  RichTextField,
  SaveActions,
  StatusPill,
  useFormValidation,
  useLanguageHotkeys,
  useLanguageVersions,
} from "@/components/Profiles";
import { RegionsPicker } from "@/components/Profiles/RegionPicker";
import { LANGUAGE_LABELS, LANGUAGES, type Website, WEBSITE_OPTIONS } from "@/constants/features";
import { editorialCategories as editorialCategoriesApi } from "@/api/editorial-categories.service";
import { usePermissions } from "@/hooks/usePermissions";
import { ROUTES } from "@/routes";
import styles from "../ProfilesForm.module.css";
import { AuditPanel } from "../Audit/AuditPanel";

const ARTICLE_TYPE_LABELS: Record<StaffArticleType, string> = {
  doing_business_in: "Doing Business In",
  focus_on: "Focus On",
  interview: "Interview",
  press_release: "Press Release",
  legal_development: "Legal Development",
};

// The nine Issue-3 profile languages (replaces the old en/fr/de trio).
const LANGUAGE_OPTIONS: SelectOption[] = LANGUAGES.map(code => ({
  value: code,
  label: LANGUAGE_LABELS[code] ?? code,
}));

const WEBSITE_SELECT_OPTIONS: SelectOption[] = WEBSITE_OPTIONS.map(w => ({
  value: w.value,
  label: w.label,
}));

/** Default publishing target when the article carries none. */
const DEFAULT_WEBSITE: Website = "com_en";

/** Look up a website's `allowedLanguages` (null = any language allowed). */
function allowedLanguagesFor(website: Website): string[] | null {
  return WEBSITE_OPTIONS.find(w => w.value === website)?.allowedLanguages ?? null;
}

const RICH_TEXT_MAX = 50_000;

// Mirror of account-ui's `ArticleDraftSchema`, trimmed to staff-collected
// fields. `editorialCountryCode` is conditional (DBI / focus_on only) so it is
// validated separately, not in the schema — exactly as account-ui (:379-382).
const ArticleDraftSchema = z.object({
  title: z.string().min(1, "Title is required"),
  content: z.string().max(RICH_TEXT_MAX).optional(),
});

type ArticleFieldKey = "title";

const ARTICLE_FIELD_KEYS = ["title"] as const satisfies ReadonlyArray<ArticleFieldKey>;

const ARTICLE_FIELD_LABELS: Record<ArticleFieldKey, string> = {
  title: "Title",
};

/** Per-language payload stored inside the `useLanguageVersions` state machine. */
type ArticleLanguagePayload = {
  title: string;
  content: string;
  status: ContentStatus;
};

function createEmptyPayload(): ArticleLanguagePayload {
  return { title: "", content: "", status: "draft" };
}

export type ArticleFormProps = {
  /** Existing article when editing; undefined when creating. */
  existing?: Article;
  /** Resolved companyId. Required (passed by callers, even in create mode). */
  companyId: string;
  /**
   * Article type for create mode. Chosen in `New.tsx` (staff routes have no
   * `:articleType` segment). Ignored when `existing` is set — the type is then
   * derived from the persisted record. The type is always locked once inside
   * the form.
   */
  initialArticleType?: StaffArticleType;
};

export function ArticleForm({ existing, companyId, initialArticleType }: ArticleFormProps) {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { hasPermission } = usePermissions();
  const canPublish = hasPermission("profiles.publish");

  const isNew = !existing;
  const isEdit = !isNew;

  // `languageCode` / `editorialCountryCode` live on the local augmentation of
  // the shared `Article`. Cast to read them on edit. The featured image now
  // rides on `Article` directly as inline `featuredImageHash`/`Filename`.
  const hydration = existing as StaffArticleHydration | undefined;

  // Locked article type: derived from the persisted record on edit, or from the
  // `initialArticleType` prop on create (chosen in New.tsx). Never editable.
  const articleType: StaffArticleType =
    (existing?.articleType as StaffArticleType | undefined) ??
    initialArticleType ??
    "doing_business_in";
  const articleTypeLabel = ARTICLE_TYPE_LABELS[articleType];

  // The editorial-category selector is shown for DBI / focus_on / legal_development
  // / interview. Kept separate from the editorial *country code* (still restricted
  // to DBI / focus_on server-side, and 400s on interview) — this form collects no
  // country code, so widening the selector alone is safe.
  const showEditorialCategories =
    articleType === "doing_business_in" ||
    articleType === "focus_on" ||
    articleType === "legal_development" ||
    articleType === "interview";
  const showInterviewFields = articleType === "interview";

  // Articles carry a single language per row today. We still use the shared hook
  // so the UX matches sibling staff forms (tab bar + active-language editor).
  // Adding a language is a client-only affordance until profiles-api supports
  // multi-language articles (mirrors account-ui's TODO).
  const initialVersions = useMemo(() => {
    if (!existing) {
      return [{ ...createEmptyPayload(), languageCode: "en" }];
    }
    const status: ContentStatus = existing.status === "published" ? "published" : "draft";
    return [
      {
        title: existing.title ?? "",
        content: existing.content ?? "",
        status,
        languageCode: hydration?.languageCode ?? "en",
      },
    ];
    // hydration is derived from existing, so existing is the only real dep.
  }, [existing, hydration?.languageCode]);

  const {
    versions,
    activeCode,
    setActiveCode,
    activeVersion,
    patchActive,
    addLanguage,
    removeLanguage,
    markSaved,
    tabOptions,
  } = useLanguageVersions<ArticleLanguagePayload>({
    initial: initialVersions,
    createEmpty: createEmptyPayload,
  });

  useLanguageHotkeys({
    codes: tabOptions.map(o => o.code),
    activeCode,
    setActiveCode,
  });

  const [featuredImage, setFeaturedImage] = useState<ImageRef | null>(
    existing?.featuredImageHash && existing?.featuredImageFilename
      ? { hash: existing.featuredImageHash, filename: existing.featuredImageFilename }
      : null,
  );
  const [regions, setRegions] = useState<RegionAssignment[]>(
    // Country-only (§4.2): ignore any legacy `kind:"region"` rows on hydration.
    (existing?.regions ?? []).filter(r => r.kind === "country"),
  );
  const [regionsError, setRegionsError] = useState<string | null>(null);
  const [website, setWebsite] = useState<Website>(existing?.website ?? DEFAULT_WEBSITE);
  const [contactId, setContactId] = useState<string | null>(existing?.contactId ?? null);
  const [editorialCategoryIds, setEditorialCategoryIds] = useState<number[]>(
    existing?.editorialCategories?.map(c => c.editorialCategoryId) ?? [],
  );
  const [showDelete, setShowDelete] = useState(false);
  const [formError, setFormError] = useState<string | null>(null);
  const [interviewError, setInterviewError] = useState<string | null>(null);

  const {
    errors: fieldErrors,
    summary,
    orphanMessages,
    validate,
    clearError,
    setServerIssues,
  } = useFormValidation({
    schema: ArticleDraftSchema,
    labels: ARTICLE_FIELD_LABELS,
    knownKeys: ARTICLE_FIELD_KEYS,
  });

  // Re-hydrate when the upstream article reference changes (e.g. after refetch).
  useEffect(() => {
    if (!existing) return;
    setFeaturedImage(
      existing.featuredImageHash && existing.featuredImageFilename
        ? { hash: existing.featuredImageHash, filename: existing.featuredImageFilename }
        : null,
    );
    setRegions(
      (existing.regions ?? [])
        .filter(r => r.kind === "country")
        .map(r => ({ kind: "country" as const, id: r.id })),
    );
    setWebsite(existing.website ?? DEFAULT_WEBSITE);
    setContactId(existing.contactId ?? null);
    setEditorialCategoryIds(existing.editorialCategories?.map(c => c.editorialCategoryId) ?? []);
  }, [existing]);

  // ── Website → Language lock (Issue 3) ──
  // fr/de websites restrict content to a single allowed language. When the
  // selected website only allows one language, the profile language is forced
  // to it (and the language Select is disabled). com_en / com_zh allow any.
  //
  // Articles carry a single language row, so "forcing" the language means
  // overriding the effective/sent language code rather than switching the
  // `useLanguageVersions` active version (which would leave `activeVersion`
  // undefined, as there is only one row). `effectiveLanguageCode` is what we
  // display in the Select and send on save.
  const allowedLanguages = allowedLanguagesFor(website);
  const languageLocked = allowedLanguages !== null && allowedLanguages.length === 1;
  const lockedLanguage = languageLocked ? allowedLanguages[0] : null;

  // `legacy_publications.site` domain of the selected Website — scopes the
  // Countries picker to what that site publishes (spec 19).
  const websiteDomain = WEBSITE_OPTIONS.find(w => w.value === website)?.domain;

  // ── Editorial category picker options (DBI / focus_on only) ──
  // The full global practice-area taxonomy (Region › optional Practice Group ›
  // Practice Area) is loaded once and handed wholesale to
  // `EditorialCategorySelect`, which does its own search / tree / chip handling.
  const allCategoriesQuery = useQuery({
    queryKey: ["editorial-categories", "all"],
    queryFn: () => editorialCategoriesApi.fetchAll(),
    enabled: showEditorialCategories,
  });

  const allCategories = useMemo<EditorialCategoryResult[]>(
    () => allCategoriesQuery.data ?? [],
    [allCategoriesQuery.data],
  );

  // ── Interview-contact typeahead (staff-only, company-scoped) ──
  const [lawyerQueryInput, setLawyerQueryInput] = useState("");
  const [debouncedLawyerQuery, setDebouncedLawyerQuery] = useState("");
  const [lawyerDropdownOpen, setLawyerDropdownOpen] = useState(false);
  const lawyerDropdownCloseTimer = useRef<number | undefined>(undefined);

  useEffect(() => () => window.clearTimeout(lawyerDropdownCloseTimer.current), []);

  useEffect(() => {
    const handle = window.setTimeout(() => {
      setDebouncedLawyerQuery(lawyerQueryInput.trim());
    }, 200);
    return () => window.clearTimeout(handle);
  }, [lawyerQueryInput]);

  const lawyersQuery = useQuery({
    queryKey: lawyerProfileKeys.list({ companyId, q: debouncedLawyerQuery || undefined }),
    queryFn: () =>
      lawyerProfilesService.list({
        companyId,
        ...(debouncedLawyerQuery ? { q: debouncedLawyerQuery } : {}),
      }),
    enabled: showInterviewFields && Boolean(companyId),
  });

  const lawyerOptions = useMemo<LawyerProfile[]>(
    () => lawyersQuery.data?.data ?? [],
    [lawyersQuery.data?.data],
  );

  const selectedLawyer = useMemo<LawyerProfile | null>(() => {
    if (contactId === null) return null;
    return lawyerOptions.find((lp: LawyerProfile) => lp.contactId === contactId) ?? null;
  }, [contactId, lawyerOptions]);

  // ── Mutations ──
  const createMutation = useMutation({
    mutationFn: (input: ArticleCreatePayload) => articlesService.create(input),
    onSuccess: async saved => {
      await queryClient.invalidateQueries({ queryKey: articleKeys.all });
      const status: ContentStatus = saved.status === "published" ? "published" : "draft";
      markSaved([
        {
          title: saved.title ?? "",
          content: saved.content ?? "",
          status,
          languageCode: (saved as StaffArticleHydration).languageCode ?? "en",
        },
      ]);
      navigate(ROUTES.profiles.articleDetail(saved.id));
    },
    onError: handleMutationError,
  });

  const updateMutation = useMutation({
    mutationFn: ({ id, input }: { id: string; input: ArticleUpdatePayload }) =>
      articlesService.update(id, input),
    onSuccess: async saved => {
      await queryClient.invalidateQueries({ queryKey: articleKeys.all });
      const status: ContentStatus = saved.status === "published" ? "published" : "draft";
      markSaved([
        {
          title: saved.title ?? "",
          content: saved.content ?? "",
          status,
          languageCode: (saved as StaffArticleHydration).languageCode ?? "en",
        },
      ]);
    },
    onError: handleMutationError,
  });

  const deleteMutation = useMutation({
    mutationFn: () => {
      if (!existing) throw new Error("No article to delete");
      return articlesService.remove(existing.id);
    },
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: articleKeys.all });
      navigate(ROUTES.profiles.articles);
    },
    onError: handleMutationError,
  });

  function handleMutationError(err: Error) {
    const issues = extractZodIssues(err);
    if (issues && issues.length > 0) {
      setServerIssues(issues);
      setFormError(null);
      return;
    }
    setFormError(err.message);
  }

  const isSaving = createMutation.isPending || updateMutation.isPending;

  function handleSave(targetStatus: ContentStatus) {
    setFormError(null);
    setRegionsError(null);
    setInterviewError(null);

    if (!activeVersion) return;

    const parsed = validate({
      title: activeVersion.title,
      content: activeVersion.content || undefined,
    });
    if (parsed === null) {
      return;
    }

    if (regions.length === 0) {
      setRegionsError("At least one region is required");
      return;
    }

    if (showInterviewFields && contactId === null) {
      setInterviewError("Contact is required for interview articles");
      return;
    }

    // Reflect the in-flight transition in the UI (SaveActions drives status).
    patchActive({ status: targetStatus });

    const basePayload: ArticleUpdatePayload = {
      title: activeVersion.title,
      articleType,
      // When the website locks the language, the locked code wins over the row's.
      languageCode: lockedLanguage ?? activeVersion.languageCode,
      website,
      content: activeVersion.content,
      status: targetStatus,
      featuredImageHash: featuredImage?.hash ?? null,
      featuredImageFilename: featuredImage?.filename ?? null,
      // Editorial categories are sent for DBI / focus_on / legal_development /
      // interview. (The editorial *country code* stays DBI/focus_on-only — the
      // API still 400s on a country code for interview — but this form never
      // collects one, so sending categories for interview is fine.)
      editorialCategories: showEditorialCategories
        ? editorialCategoryIds.map(id => ({ editorialCategoryId: id }))
        : undefined,
      contactId: showInterviewFields ? (contactId ?? undefined) : undefined,
      regions,
    };

    if (isNew) {
      createMutation.mutate({ ...basePayload, companyId } as ArticleCreatePayload);
    } else if (existing) {
      updateMutation.mutate({ id: existing.id, input: basePayload });
    }
  }

  function handleFormSubmit(e: React.FormEvent) {
    e.preventDefault();
    handleSave(activeVersion?.status ?? "draft");
  }

  const languageSelectOptions: SelectOption[] = LANGUAGE_OPTIONS.map(o =>
    activeVersion && o.value === activeVersion.languageCode
      ? { ...o, label: labelForLanguage(activeVersion.languageCode) }
      : o,
  );

  const availableLanguages = LANGUAGE_OPTIONS.filter(
    o => typeof o.value === "string" && o.value && !versions.some(v => v.languageCode === o.value),
  ).map(o => ({ code: String(o.value), label: o.label }));

  if (!activeVersion) {
    return (
      <p role="status" className={styles.muted}>
        Loading…
      </p>
    );
  }

  const currentStatus = activeVersion.status;

  return (
    <>
      <div className={styles.headerActions}>
        <Button variant="outlined" type="button" onClick={() => navigate(ROUTES.profiles.articles)}>
          Cancel
        </Button>
        <SaveActions
          layout="inline"
          currentStatus={currentStatus}
          canPublish={canPublish}
          isSaving={isSaving}
          onSave={handleSave}
          onDelete={isEdit ? () => setShowDelete(true) : undefined}
        />
      </div>

      <FormValidationSummary entries={summary} />

      {(formError || orphanMessages.length > 0) && (
        <div role="alert" className={styles.errorBanner}>
          {formError ?? orphanMessages.join("\n")}
        </div>
      )}

      <LanguageTabBar
        languages={tabOptions}
        activeCode={activeCode}
        onChange={setActiveCode}
        availableLanguages={availableLanguages}
        onAdd={addLanguage}
        onRemove={versions.length > 1 ? removeLanguage : undefined}
        addLabel="Add language"
      />

      <form onSubmit={handleFormSubmit} aria-label={isNew ? "Create article" : "Edit article"}>
        <div className={styles.layout}>
          <div className={styles.main}>
            <section className={styles.section}>
              <h2 className={styles.sectionTitle}>Basics</h2>
              <div className={styles.fieldGridSingle}>
                <Input
                  label="Title"
                  value={activeVersion.title}
                  onChange={e => {
                    patchActive({ title: e.target.value });
                    clearError("title");
                  }}
                  required
                  aria-required="true"
                  error={fieldErrors.title}
                  fullWidth
                  data-testid="article-title"
                />

                <div>
                  <span className={styles.labelHint} id="article-type-label">
                    Article type
                  </span>
                  <div>
                    <span
                      className={styles.muted}
                      data-testid="article-type-pill"
                      aria-labelledby="article-type-label"
                      aria-live="polite"
                    >
                      {articleTypeLabel}
                    </span>
                  </div>
                </div>

                <Select
                  label="Website"
                  value={website}
                  options={WEBSITE_SELECT_OPTIONS}
                  fullWidth
                  aria-label="Website"
                  data-testid="article-website"
                  onChange={e => setWebsite(e.target.value as Website)}
                />

                <Select
                  label="Language"
                  value={lockedLanguage ?? activeVersion.languageCode}
                  options={languageSelectOptions}
                  fullWidth
                  aria-label="Language"
                  data-testid="article-language"
                  disabled={languageLocked}
                  onChange={e => setActiveCode(e.target.value)}
                />
              </div>
            </section>

            <section className={styles.section}>
              <h2 className={styles.sectionTitle}>Featured image</h2>
              <ImageUploadField
                label="Hero image"
                value={featuredImage}
                onChange={setFeaturedImage}
                slug={activeVersion.title}
              />
            </section>

            <section className={styles.section}>
              <h2 className={styles.sectionTitle}>Content</h2>
              <RichTextField
                label="Body"
                preset="basic_enhanced"
                mediaEnabled={false}
                value={activeVersion.content}
                onChange={html => patchActive({ content: html })}
              />
            </section>

            {showEditorialCategories && (
              <section className={styles.section} data-testid="editorial-section">
                <h2 className={styles.sectionTitle}>Editorial</h2>
                <EditorialCategorySelect
                  label="Editorial categories"
                  value={editorialCategoryIds}
                  onChange={setEditorialCategoryIds}
                  options={allCategories}
                />
              </section>
            )}

            {showInterviewFields && (
              <section className={styles.section} data-testid="interview-contact-section">
                <h2 className={styles.sectionTitle}>Interview</h2>
                <div className={styles.comboboxContainer}>
                  <Input
                    label="Contact (interviewee)"
                    value={
                      selectedLawyer
                        ? `${selectedLawyer.firstName} ${selectedLawyer.lastName}`.trim()
                        : lawyerQueryInput
                    }
                    onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                      if (selectedLawyer) {
                        // Editing the search term implicitly clears the selection.
                        setContactId(null);
                      }
                      setLawyerQueryInput(e.target.value);
                      setLawyerDropdownOpen(true);
                    }}
                    onFocus={() => setLawyerDropdownOpen(true)}
                    onBlur={() => {
                      window.clearTimeout(lawyerDropdownCloseTimer.current);
                      lawyerDropdownCloseTimer.current = window.setTimeout(
                        () => setLawyerDropdownOpen(false),
                        120,
                      );
                    }}
                    placeholder="Search lawyers for this firm…"
                    aria-haspopup="listbox"
                    aria-expanded={lawyerDropdownOpen}
                    fullWidth
                    data-testid="interview-contact-input"
                  />
                  {lawyerDropdownOpen && (
                    <ul
                      role="listbox"
                      aria-label="Lawyer search results"
                      data-testid="interview-contact-options"
                      className={styles.comboboxList}
                    >
                      {lawyersQuery.isPending && (
                        <li className={styles.comboboxStatus}>Loading lawyers…</li>
                      )}
                      {!lawyersQuery.isPending && lawyerOptions.length === 0 && (
                        <li className={styles.comboboxStatus}>No lawyers for this firm</li>
                      )}
                      {lawyerOptions.map((lp: LawyerProfile) => {
                        const label = `${lp.firstName} ${lp.lastName}`.trim();
                        const key = String(lp.contactId ?? lp.id);
                        return (
                          <li
                            key={key}
                            role="option"
                            aria-selected={selectedLawyer?.id === lp.id}
                            tabIndex={0}
                            onMouseDown={e => {
                              e.preventDefault();
                              setContactId(lp.contactId ?? null);
                              setLawyerQueryInput("");
                              setLawyerDropdownOpen(false);
                            }}
                            onKeyDown={(e: React.KeyboardEvent<HTMLLIElement>) => {
                              if (e.key === "Enter" || e.key === " ") {
                                e.preventDefault();
                                setContactId(lp.contactId ?? null);
                                setLawyerQueryInput("");
                                setLawyerDropdownOpen(false);
                              }
                            }}
                            className={styles.comboboxItem}
                          >
                            <div className={styles.comboboxItemPrimary}>
                              {label || `(unnamed) ${lp.id}`}
                            </div>
                            {lp.position && (
                              <div className={styles.comboboxItemMeta}>{lp.position}</div>
                            )}
                          </li>
                        );
                      })}
                    </ul>
                  )}
                </div>
                {interviewError && (
                  <p role="alert" className={styles.errorBanner}>
                    {interviewError}
                  </p>
                )}
              </section>
            )}
          </div>

          <aside className={styles.sidebar}>
            <section className={styles.section}>
              <h2 className={styles.sectionTitle}>Status</h2>
              <StatusPill status={currentStatus} />
            </section>

            <section className={styles.section}>
              <h2 className={styles.sectionTitle}>Countries</h2>
              <RegionsPicker
                label="Countries (at least one required)"
                site={websiteDomain}
                value={regions}
                onChange={next => {
                  setRegions(next);
                  if (next.length > 0) setRegionsError(null);
                }}
                error={regionsError}
              />
            </section>

            {existing && <AuditPanel entityType="article" entityId={existing.id} />}
          </aside>
        </div>
      </form>

      {showDelete && existing && (
        <ConfirmationModal
          title="Delete article"
          message={`Delete article '${existing.title ?? "untitled"}'?`}
          warningMessage="This action cannot be undone."
          confirmButtonText="Delete"
          confirmButtonColor="error"
          cancelButtonText="Cancel"
          onClose={() => setShowDelete(false)}
          onConfirm={async () => {
            await deleteMutation.mutateAsync();
          }}
        />
      )}
    </>
  );
}

export default ArticleForm;
